import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Plus, Pencil, Trash2, Search, Loader2, CreditCard, MessageCircle, Clock, CalendarClock } from 'lucide-react';
import { useConfirmDialog } from '@/components/useConfirmDialog';

const initialForm = { name: '', email: '', phone: '', plano_gestor_id: '', status: 'ativo' };

function formatWhatsApp(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return null;
  const num = digits.length <= 11 ? '55' + digits : digits;
  return num;
}

function buildWhatsAppLink(phone, message) {
  const num = formatWhatsApp(phone);
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
}

function msgLembrete(nome) {
  return `Olá, ${nome}! Tudo bem?\n\nGostaria de lembrar que a mensalidade do seu plano na plataforma vence em 2 dias.\n\nCaso precise de suporte ou tenha qualquer dúvida, estou à disposição.\n\nAgradeço pela parceria.`;
}

function msgVenceHoje(nome) {
  return `Olá, ${nome}! Tudo bem?\n\nPassando para lembrar que a mensalidade do seu plano vence hoje.\n\nPeço que realize o pagamento dentro do prazo para evitar bloqueio de acesso ao sistema.\n\nQualquer dúvida, estou à disposição.`;
}

export default function GestoresPage() {
  const [gestores, setGestores] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [planoDialog, setPlanoDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [planoId, setPlanoId] = useState('');
  const [savingPlano, setSavingPlano] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/admin/gestores'),
      api.get('/admin/planos-plataforma')
    ]).then(([g, p]) => {
      setGestores(g.data);
      setPlanos(p.data);
    }).catch(() => toast.error('Erro ao carregar dados')).finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    const res = await api.get('/admin/gestores');
    setGestores(res.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/gestores/${editing.id}`, form);
        toast.success('Gestor atualizado');
      } else {
        await api.post('/admin/gestores', form);
        toast.success('Gestor criado');
      }
      setDialogOpen(false);
      setEditing(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!await confirm('Remover Gestor', 'Tem certeza que deseja remover este gestor e todos os dados do tenant?')) return;
    try {
      await api.delete(`/admin/gestores/${id}`);
      toast.success('Gestor removido');
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openCreate = () => { setEditing(null); setForm(initialForm); setDialogOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name, email: g.email, phone: g.phone || '', plano_gestor_id: g.plano_gestor_id || '', status: g.status || 'ativo' }); setDialogOpen(true); };
  
  const openPlanoDialog = (g) => { setPlanoDialog(g); setPlanoId(g.plano_gestor_id || ''); };
  const vincularPlano = async () => {
    if (!planoId) { toast.error('Selecione um plano'); return; }
    setSavingPlano(true);
    try {
      await api.put(`/admin/gestores/${planoDialog.id}/vincular-plano`, { plano_gestor_id: planoId });
      toast.success('Plano vinculado!');
      setPlanoDialog(null);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingPlano(false);
  };

  const filtered = gestores.filter(g => g.name?.toLowerCase().includes(search.toLowerCase()) || g.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <TooltipProvider>
    <div className="space-y-6 animate-fadeIn" data-testid="gestores-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">GESTORES</h1>
        <Button onClick={openCreate} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="add-gestor-button">
          <Plus className="w-4 h-4 mr-2" /> Novo Gestor
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input className="pl-10 bg-[#0A0A0A] border-[#27272A]" placeholder="Buscar gestores..." value={search} onChange={e => setSearch(e.target.value)} data-testid="gestores-search" />
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-zinc-400">Nome</TableHead>
                <TableHead className="text-zinc-400">Email</TableHead>
                <TableHead className="text-zinc-400">Telefone</TableHead>
                <TableHead className="text-zinc-400">Plano</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400 text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Carregando...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">Nenhum gestor encontrado</TableCell></TableRow>
              ) : filtered.map(g => {
                const hasPhone = !!formatWhatsApp(g.phone);
                return (
                <TableRow key={g.id} className="border-[#27272A] hover:bg-[#1C1C1E]" data-testid={`gestor-row-${g.id}`}>
                  <TableCell className="text-white font-medium">{g.name}</TableCell>
                  <TableCell className="text-zinc-400">{g.email}</TableCell>
                  <TableCell className="text-zinc-400">{g.phone || '-'}</TableCell>
                  <TableCell>
                    {g.plano_nome ? (
                      <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-[#007AFF]/20 cursor-pointer" onClick={() => openPlanoDialog(g)} data-testid={`gestor-plano-${g.id}`}>
                        {g.plano_nome}
                      </Badge>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 text-xs h-6 px-2" onClick={() => openPlanoDialog(g)} data-testid={`vincular-plano-gestor-${g.id}`}>
                        <CreditCard className="w-3 h-3 mr-1" /> Vincular
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-md font-medium ${g.status === 'ativo' ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`}>{g.status}</span>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" className={hasPhone ? 'text-green-400 hover:text-green-300' : 'text-zinc-600 cursor-not-allowed'} disabled={!hasPhone} onClick={() => hasPhone && window.open(buildWhatsAppLink(g.phone, msgLembrete(g.name)), '_blank')} data-testid={`wa-lembrete-gestor-${g.id}`}>
                          <Clock className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#141414] border-[#27272A]"><p className="text-xs">Lembrar (2 dias)</p></TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="sm" variant="ghost" className={hasPhone ? 'text-amber-400 hover:text-amber-300' : 'text-zinc-600 cursor-not-allowed'} disabled={!hasPhone} onClick={() => hasPhone && window.open(buildWhatsAppLink(g.phone, msgVenceHoje(g.name)), '_blank')} data-testid={`wa-vencehoje-gestor-${g.id}`}>
                          <CalendarClock className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#141414] border-[#27272A]"><p className="text-xs">Vence hoje</p></TooltipContent>
                    </Tooltip>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(g)} className="text-zinc-400 hover:text-white" data-testid={`edit-gestor-${g.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(g.id)} className="text-zinc-400 hover:text-red-400" data-testid={`delete-gestor-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">{editing ? 'Editar Gestor' : 'Novo Gestor'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="gestor-form-name" /></div>
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" required data-testid="gestor-form-email" /></div>
            {!editing && <p className="text-xs text-zinc-500">Senha padrao: 12345678</p>}
            <div><Label className="text-zinc-400 text-xs uppercase tracking-wider">Telefone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-[#0A0A0A] border-[#27272A]" placeholder="(11) 99999-9999" data-testid="gestor-form-phone" /></div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Plano da Plataforma</Label>
              <Select value={form.plano_gestor_id || 'none'} onValueChange={v => setForm({...form, plano_gestor_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="gestor-form-plano"><SelectValue placeholder="Selecione um plano" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">
                  <SelectItem value="none">Sem plano</SelectItem>
                  {planos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} - R$ {(p.valor || 0).toFixed(2)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="gestor-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#27272A]">
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                    <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="gestor-form-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Salvar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vincular Plano Dialog */}
      <Dialog open={!!planoDialog} onOpenChange={() => setPlanoDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">Vincular Plano</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Gestor: <span className="text-white font-medium">{planoDialog?.name}</span></p>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Plano da Plataforma</Label>
              <Select value={planoId || 'none'} onValueChange={v => setPlanoId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]" data-testid="vincular-plano-select">
                  <SelectValue placeholder="Selecione um plano" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">
                  <SelectItem value="none">Sem plano</SelectItem>
                  {planos.map(p => <SelectItem key={p.id} value={p.id}>{p.nome} - R$ {(p.valor || 0).toFixed(2)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPlanoDialog(null)} className="text-zinc-400">Cancelar</Button>
              <Button onClick={vincularPlano} disabled={savingPlano} className="bg-[#007AFF] hover:bg-[#0066DD]" data-testid="vincular-plano-submit">
                {savingPlano ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vincular'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    <ConfirmDialog />
    </TooltipProvider>
  );
}
